import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useState, Children } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { SCROLL_POSITION_SCROLL_MODE, TARGET_SCROLL_MODE } from './ScrollElement.constants';
import { StylePropType } from '../../../../services/proptypes';
import { classNames } from '../../../../utils/classname';
import { isServerSideRendering } from '../../../../utils/gatsby';
import useConditionalClassOnDomElements from '../../hooks/use-conditional-class-on-dom-elements';
import useScroll from '../../hooks/use-scroll';
import { createComponent } from '../../services/components';
import { ChildrenPropType } from '../../types/component';
import './ScrollElement.less';
var propTypes = {
  scrollTarget: PropTypes.string.isRequired,
  targetPositionProperty: PropTypes.oneOf(['top', 'bottom']),
  scrollMode: PropTypes.oneOf([TARGET_SCROLL_MODE, SCROLL_POSITION_SCROLL_MODE]),
  offset: PropTypes.number,
  className: PropTypes.string,
  style: StylePropType,
  children: ChildrenPropType.isRequired,
  applyOnlyToParent: PropTypes.bool,
  listenerNodeIds: PropTypes.arrayOf(PropTypes.string)
};
var defaultProps = {
  className: '',
  targetPositionProperty: 'bottom',
  scrollMode: TARGET_SCROLL_MODE,
  offset: 0,
  style: {},
  applyOnlyToParent: false,
  listenerNodeIds: []
};
export default function ScrollElement(_ref) {
  var scrollTarget = _ref.scrollTarget,
    targetPositionProperty = _ref.targetPositionProperty,
    scrollMode = _ref.scrollMode,
    offset = _ref.offset,
    className = _ref.className,
    style = _ref.style,
    children = _ref.children,
    applyOnlyToParent = _ref.applyOnlyToParent,
    listenerNodeIds = _ref.listenerNodeIds;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasScrolled = _useState2[0],
    setHasScrolled = _useState2[1];
  function getTargetPosition(target) {
    var topPosition = _get(target, 'offsetTop');
    var offsetHeight = _get(target, 'offsetHeight');
    if (targetPositionProperty === 'top') {
      return topPosition;
    }
    return topPosition + offsetHeight;
  }
  function handleScroll() {
    if (isServerSideRendering()) {
      return;
    }
    var target = document.querySelector(scrollTarget);
    if (_isEmpty(target)) {
      return;
    }
    var targetPosition = getTargetPosition(target);
    var currentPosition = window.scrollY;
    if (scrollMode === TARGET_SCROLL_MODE) {
      if (currentPosition + offset > targetPosition) {
        if (!hasScrolled) {
          setHasScrolled(true);
        }
      } else if (hasScrolled) {
        setHasScrolled(false);
      }
    }
    if (scrollMode === SCROLL_POSITION_SCROLL_MODE) {
      if (currentPosition > offset) {
        if (!hasScrolled) {
          setHasScrolled(true);
        }
      } else if (hasScrolled) {
        setHasScrolled(false);
      }
    }
  }
  function applyStylesToNode(node) {
    var _classNames;
    var _node$props = node.props,
      props = _node$props === void 0 ? {} : _node$props;
    var _props$className = props.className,
      currentClassName = _props$className === void 0 ? '' : _props$className,
      _props$style = props.style,
      currentStyle = _props$style === void 0 ? {} : _props$style;
    var finalClassName = hasScrolled ? classNames((_classNames = {}, _defineProperty(_classNames, currentClassName, true), _defineProperty(_classNames, className, true), _classNames)) : currentClassName;
    var finalStyle = hasScrolled ? _objectSpread(_objectSpread({}, currentStyle), style) : currentStyle;
    return createComponent(node, {
      className: finalClassName,
      style: finalStyle
    });
  }
  function renderChildren() {
    if (applyOnlyToParent) {
      return applyStylesToNode(children);
    }
    return Children.map(children, function (child) {
      if ( /*#__PURE__*/React.isValidElement(child)) {
        return applyStylesToNode(child);
      }
      return child;
    });
  }
  useScroll({
    onScroll: handleScroll
  });
  useConditionalClassOnDomElements(hasScrolled, className, listenerNodeIds);
  return renderChildren();
}
ScrollElement.propTypes = propTypes;
ScrollElement.defaultProps = defaultProps;